import React, { useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { MainLayout } from '@/components/layout/MainLayout'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  PlusCircle,
  Loader2,
  ArrowRight,
  CheckCircle,
  Users,
  Calendar,
  Layers3,
  Sparkles,
  MoreVertical,
  Archive,
  Trash2,
} from 'lucide-react'
import {
  useGetApplicationsQuery,
  useGetMyInvitationsQuery,
  useGetCampaignsQuery,
  useGetCollaborationsQuery,
  useArchiveCampaignMutation,
  useDeleteCampaignMutation,
} from '@/redux/marketerApi'
import {
  ActiveTable,
  ArchivedTable,
  CompletedList,
  OffersTable,
} from '@/components/campaigns/DealTables'
import { SubmissionCard } from '@/components/campaigns/SubmissionCard'
import { InvitationCard } from '@/components/campaigns/InvitationCard'
import { formatDateOnly } from '@/lib/dateOnly'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useToast } from '@/hooks/use-toast'

const CAMPAIGN_ACCENTS = [
  {
    solid: '#C0FD72',
    soft: 'rgba(192, 253, 114, 0.16)',
    border: 'rgba(192, 253, 114, 0.32)',
  },
  {
    solid: '#3ACEFF',
    soft: 'rgba(58, 206, 255, 0.16)',
    border: 'rgba(58, 206, 255, 0.32)',
  },
  {
    solid: '#A87FF4',
    soft: 'rgba(168, 127, 244, 0.16)',
    border: 'rgba(168, 127, 244, 0.32)',
  },
  {
    solid: '#F9DA4C',
    soft: 'rgba(249, 218, 76, 0.18)',
    border: 'rgba(249, 218, 76, 0.32)',
  },
  {
    solid: '#9CF0FB',
    soft: 'rgba(156, 240, 251, 0.16)',
    border: 'rgba(156, 240, 251, 0.32)',
  },
  {
    solid: '#7255F2',
    soft: 'rgba(114, 85, 242, 0.18)',
    border: 'rgba(114, 85, 242, 0.34)',
  },
]

const getCampaignInitials = (name: string) =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('')

const formatCampaignDateRange = (startDate?: string | null, endDate?: string | null) => {
  const start = formatDateOnly(startDate, 'en-US', {
    month: 'numeric',
    day: 'numeric',
    year: '2-digit',
  })
  const end = formatDateOnly(endDate, 'en-US', {
    month: 'numeric',
    day: 'numeric',
    year: '2-digit',
  })

  if (start && end) return `${start} – ${end}`
  return start || end || null
}

const TABS = [
  { id: 'campaigns', label: 'Campaigns', alert: false },
  { id: 'incoming', label: 'Incoming', alert: true },
  { id: 'offers', label: 'Offers sent', alert: false },
  { id: 'active', label: 'Active deals', alert: false },
  { id: 'completed', label: 'Completed', alert: false },
  { id: 'archived', label: 'Archived', alert: false },
] as const
type TabId = (typeof TABS)[number]['id']

type PendingAction = { type: 'archive' | 'delete'; id: string; name: string } | null

const Campaigns: React.FC = () => {
  const navigate = useNavigate()
  const { toast } = useToast()

  // One hub for everything about who the brand works with: campaign records, what athletes
  // sent in, offers the brand sent out, deals being worked, and finished work.
  const [params, setParams] = useSearchParams()
  const tab = (TABS.find((t) => t.id === params.get('tab'))?.id ?? 'campaigns') as TabId
  const setTab = (id: TabId) => setParams(id === 'campaigns' ? {} : { tab: id }, { replace: true })
  const [pendingAction, setPendingAction] = useState<PendingAction>(null)
  const { data: applications = [] } = useGetApplicationsQuery()
  const { data: invitationData } = useGetMyInvitationsQuery()
  const invitations = invitationData?.invitations ?? []

  const { data: campaigns = [], isLoading: campaignsLoading } = useGetCampaignsQuery()
  const { data: allCollaborations = [], isLoading: listLoading } = useGetCollaborationsQuery()
  // Deals this user archived leave every working tab and wait in Archived, newest archived first
  const collaborations = allCollaborations.filter((c) => !c.archived)
  const archivedCollabs = allCollaborations
    .filter((c) => c.archived)
    .sort((a, b) => String(b.archivedAt ?? '').localeCompare(String(a.archivedAt ?? '')))
  const [archiveCampaign, archiveState] = useArchiveCampaignMutation()
  const [deleteCampaign, deleteState] = useDeleteCampaignMutation()
  const loading = campaignsLoading

  const handleConfirmAction = async () => {
    if (!pendingAction) return
    const { type, id, name } = pendingAction
    try {
      if (type === 'archive') {
        await archiveCampaign(id).unwrap()
        toast({ title: 'Campaign archived', description: `"${name}" was archived.` })
      } else {
        await deleteCampaign(id).unwrap()
        toast({ title: 'Campaign deleted', description: `"${name}" was deleted.` })
      }
      setPendingAction(null)
    } catch (err: unknown) {
      const apiError = err as { data?: { error?: string } } | undefined
      const message = apiError?.data?.error || 'Action failed. Please try again.'
      toast({ title: 'Could not complete action', description: message, variant: 'destructive' })
      setPendingAction(null)
    }
  }

  const campaignSummary = useMemo(() => {
    const liveCount = campaigns.filter((campaign) => {
      const normalized = campaign.status?.toLowerCase()
      return normalized === 'active' || normalized === 'published' || normalized === 'live'
    }).length

    return {
      total: campaigns.length,
      live: liveCount,
      totalCollaborations: campaigns.reduce(
        (sum, campaign) => sum + (campaign.collaborationCount ?? 0),
        0,
      ),
    }
  }, [campaigns])

  // Filter collaborations for tabs
  const actionRequiredCollabs = collaborations.filter((c) => {
    if (['offered', 'rejected', 'expired', 'withdrawn'].includes(c.status)) return false
    if (c.isComplete) return false
    return c.actionOwner === 'marketer' || c.hasPendingReview
  })

  const waitingCollabs = collaborations.filter((c) => {
    if (['offered', 'rejected', 'expired', 'withdrawn'].includes(c.status)) return false
    if (c.isComplete) return false
    return c.actionOwner === 'athlete'
  })

  // Pending offers are technically waiting on athlete too, but might want to separate them later
  // For now let's put offers in "Waiting on Athlete" or their own bucket?
  // Plan said "Pending Response" separate or "Waiting on Athlete". Let's include offers in Waiting.
  const pendingOffers = collaborations.filter((c) => c.status === 'offered')

  const completedCollabs = collaborations.filter(
    (c) => c.isComplete || c.status === 'completed' || c.status === 'live',
  )

  const yourMove = collaborations.filter(
    (c) =>
      c.status === 'accepted' &&
      !c.isComplete &&
      (c.actionOwner === 'marketer' || c.hasPendingReview),
  )
  const withAthlete = collaborations.filter(
    (c) => c.status === 'accepted' && !c.isComplete && !yourMove.includes(c),
  )
  const counts: Record<TabId, number> = {
    campaigns: campaigns.length,
    incoming: applications.length + invitations.length,
    offers: pendingOffers.length,
    active: yourMove.length + withAthlete.length,
    completed: completedCollabs.length,
    archived: archivedCollabs.length,
  }

  const handleCampaignView = (campaignId: string) => {
    navigate(`/campaign/${campaignId}`)
  }

  const renderCampaignsView = () => (
    <div className="space-y-4">
      {campaigns.map((campaign, index) => {
        const accent = CAMPAIGN_ACCENTS[index % CAMPAIGN_ACCENTS.length]
        const dateRange = formatCampaignDateRange(campaign.startDate, campaign.endDate)
        const collaborationCount = campaign.collaborationCount ?? 0
        const unreadCount = campaign.unreadNotificationCount ?? 0
        const initials = getCampaignInitials(campaign.name)
        const canDelete = collaborationCount === 0

        return (
          <div
            key={campaign.id}
            role="button"
            tabIndex={0}
            onClick={() => handleCampaignView(campaign.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                handleCampaignView(campaign.id)
              }
            }}
            className="group relative w-full cursor-pointer overflow-hidden rounded-[1.75rem] border border-foreground/10 bg-card/90 p-4 text-left transition-all duration-200 hover:border-foreground/20 hover:bg-card hover:shadow-[0_18px_50px_rgba(0,0,0,0.28)] md:p-5"
          >
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:gap-5">
              <div className="flex items-center gap-4 min-w-0 flex-1">
                <div
                  className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-[1.35rem] border text-xl font-semibold tracking-tight text-foreground md:h-[72px] md:w-[72px] md:text-2xl"
                  style={{ backgroundColor: accent.soft, borderColor: accent.border }}
                >
                  {campaign.brand?.logo ? (
                    <img
                      src={campaign.brand.logo}
                      alt={campaign.brand.name || campaign.name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    initials || 'C'
                  )}
                </div>

                <div className="min-w-0 flex-1 space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="truncate text-lg font-semibold tracking-tight text-foreground md:text-xl">
                      {campaign.name}
                    </h3>
                    {unreadCount > 0 && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-[#C0FD72]/15 border border-[#C0FD72]/30 px-2 py-0.5 text-xs font-medium text-[#3F7A0A]">
                        <span className="h-1.5 w-1.5 rounded-full bg-[#C0FD72]" />
                        {unreadCount} new
                      </span>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">
                    <span className="truncate text-gray-700">
                      {campaign.brand?.name || 'Brand campaign'}
                    </span>
                    {dateRange && (
                      <span className="flex items-center gap-1.5">
                        <Calendar className="h-3.5 w-3.5 text-gray-500" />
                        {dateRange}
                      </span>
                    )}
                    <span className="flex items-center gap-1.5">
                      <Users className="h-3.5 w-3.5 text-gray-500" />
                      {collaborationCount}{' '}
                      {collaborationCount === 1 ? 'collaboration' : 'collaborations'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between gap-3 md:min-w-[260px] md:justify-end">
                <div className="rounded-2xl border border-foreground/10 bg-card px-3 py-2 text-left md:min-w-[144px] md:text-right">
                  <div className="text-xs uppercase tracking-[0.18em] text-gray-500">
                    Collaborations
                  </div>
                  <div className="mt-1 text-base font-semibold text-foreground">
                    {collaborationCount}
                  </div>
                </div>

                <div className="flex items-center gap-2 rounded-full border border-foreground/10 bg-card px-4 py-2 text-sm font-medium text-foreground transition-colors group-hover:border-[#C0FD72]/35 group-hover:bg-[#C0FD72]/10 group-hover:text-[#3F7A0A]">
                  Open
                  <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                </div>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      onClick={(e) => e.stopPropagation()}
                      className="flex h-9 w-9 items-center justify-center rounded-full border border-foreground/10 bg-card text-gray-700 transition-colors hover:border-foreground/20 hover:bg-foreground/10 hover:text-foreground"
                      aria-label="Campaign actions"
                    >
                      <MoreVertical className="h-4 w-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenuItem
                      onSelect={() =>
                        setPendingAction({
                          type: 'archive',
                          id: campaign.id,
                          name: campaign.name,
                        })
                      }
                    >
                      <Archive className="mr-2 h-4 w-4" />
                      Archive
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      disabled={!canDelete}
                      onSelect={() => {
                        if (!canDelete) return
                        setPendingAction({
                          type: 'delete',
                          id: campaign.id,
                          name: campaign.name,
                        })
                      }}
                      className="text-red-600 focus:text-red-700"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      {canDelete ? 'Delete' : 'Delete (has collaborations)'}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>
        )
      })}

      {campaigns.length === 0 && !loading && (
        <div className="rounded-[1.75rem] border border-dashed border-foreground/12 bg-card px-6 py-12 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#C0FD72]/14 text-[#3F7A0A]">
            <Layers3 className="h-5 w-5" />
          </div>
          <h3 className="text-lg font-semibold text-foreground">No campaigns yet</h3>
          <p className="mx-auto mt-2 max-w-md text-sm text-gray-600">
            Launch your first campaign to start inviting athletes and tracking active work from one
            place.
          </p>
          <Button onClick={() => navigate('/campaign/create')} variant="lime" className="mt-6">
            <PlusCircle className="mr-2 h-4 w-4" />
            Create Campaign
          </Button>
        </div>
      )}
    </div>
  )

  const Empty: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <div className="rounded-3xl border border-dashed border-foreground/15 px-6 py-14 text-center">
      <h3 className="text-lg font-semibold text-foreground">{title}</h3>
      <p className="mx-auto mt-2 max-w-md text-sm text-gray-600">{children}</p>
    </div>
  )

  const Section: React.FC<{ title: string; count: number; children: React.ReactNode }> = ({
    title,
    count,
    children,
  }) =>
    count ? (
      <section className="space-y-3">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-500">
          {title} · {count}
        </h3>
        {children}
      </section>
    ) : null

  const renderIncoming = () =>
    counts.incoming ? (
      <div className="space-y-8">
        <Section title="Applications and pitches from athletes" count={applications.length}>
          <ul className="space-y-3">
            {applications.map((item) => (
              <SubmissionCard key={item.id} item={item} />
            ))}
          </ul>
        </Section>
        <Section title="Invitations to join a deal" count={invitations.length}>
          <div className="space-y-4">
            {invitations.map((invitation) => (
              <InvitationCard key={invitation.id} invitation={invitation} />
            ))}
          </div>
        </Section>
      </div>
    ) : (
      <Empty title="Nothing incoming right now">
        Open a campaign to applications and athletes who fit will apply here. Athletes and their
        managers can also pitch you or invite you onto a deal.
      </Empty>
    )

  const renderOffers = () =>
    pendingOffers.length ? (
      <OffersTable list={pendingOffers} />
    ) : (
      <Empty title="No offers out">
        Offers you send athletes wait here until they accept, counter or send their rate.
      </Empty>
    )

  const renderActive = () =>
    counts.active ? (
      <div className="space-y-8">
        {yourMove.length ? <ActiveTable title="Your move" list={yourMove} /> : null}
        {withAthlete.length ? (
          <ActiveTable title="Athlete is working on it" list={withAthlete} />
        ) : null}
      </div>
    ) : (
      <Empty title="No deals in progress">
        Once an athlete accepts an offer or you accept their application, the deal is tracked here
        step by step.
      </Empty>
    )

  const renderCompleted = () =>
    completedCollabs.length ? (
      <CompletedList list={completedCollabs} />
    ) : (
      <Empty title="Nothing finished yet">Completed deals and their content collect here.</Empty>
    )

  const renderArchived = () =>
    archivedCollabs.length ? (
      <ArchivedTable list={archivedCollabs} />
    ) : (
      <Empty title="Nothing archived">
        Archive a deal from any list to move it here, out of your way. Only your lists change: the
        athlete and their manager still see it. Unarchive brings it back.
      </Empty>
    )

  return (
    <MainLayout>
      <div className="mx-auto w-full max-w-[1400px] space-y-6 px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-foreground">Campaigns</h1>
            <p className="mt-1 text-gray-500">
              Your campaigns, what athletes send you, the offers you send them, and every deal from
              start to finish.
            </p>
          </div>
          <Button onClick={() => navigate('/campaign/create')} variant="lime" className="h-11">
            <PlusCircle className="mr-2 h-4 w-4" />
            Create opportunity
          </Button>
        </div>

        <nav
          role="tablist"
          aria-label="Campaigns"
          className="flex flex-wrap gap-1 rounded-2xl border border-foreground/10 bg-card p-1 shadow-[0_1px_2px_rgba(30,41,59,0.04)]"
        >
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition-colors ${
                tab === t.id ? 'bg-[#C0FD72] text-black' : 'text-gray-500 hover:text-foreground'
              }`}
            >
              {t.label}
              {counts[t.id] ? (
                <span
                  className={`min-w-5 rounded-full px-1.5 text-xs tabular-nums ${
                    t.alert && tab !== t.id
                      ? 'bg-[#DC2626] text-[#fff]'
                      : tab === t.id
                        ? 'bg-black/10'
                        : 'bg-muted'
                  }`}
                >
                  {counts[t.id]}
                </span>
              ) : null}
            </button>
          ))}
        </nav>

        {loading || listLoading ? (
          <div className="flex min-h-[300px] items-center justify-center">
            <Loader2 className="h-10 w-10 animate-spin text-gray-600" />
          </div>
        ) : tab === 'campaigns' ? (
          renderCampaignsView()
        ) : tab === 'incoming' ? (
          renderIncoming()
        ) : tab === 'offers' ? (
          renderOffers()
        ) : tab === 'active' ? (
          renderActive()
        ) : tab === 'archived' ? (
          renderArchived()
        ) : (
          renderCompleted()
        )}
      </div>

      <AlertDialog
        open={pendingAction !== null}
        onOpenChange={(open) => !open && setPendingAction(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingAction?.type === 'delete' ? 'Delete campaign?' : 'Archive campaign?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingAction?.type === 'delete'
                ? `"${pendingAction?.name}" will be permanently deleted. This cannot be undone.`
                : `"${pendingAction?.name}" will be hidden from your campaigns list. An admin can restore it later.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={archiveState.isLoading || deleteState.isLoading}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault()
                handleConfirmAction()
              }}
              disabled={archiveState.isLoading || deleteState.isLoading}
              className={
                pendingAction?.type === 'delete'
                  ? 'bg-red-400 text-foreground hover:bg-red-300'
                  : undefined
              }
            >
              {pendingAction?.type === 'delete' ? 'Delete' : 'Archive'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </MainLayout>
  )
}

export default Campaigns
